import classNames from 'classnames';
import omit from 'lodash.omit';
import PropTypes from 'prop-types';
import React from 'react';
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import MediaQuery from 'react-responsive';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import {connect} from 'react-redux';
import tabStyles from 'react-tabs/style/react-tabs.css';
import VM from 'scratch-vm';
import Renderer from 'scratch-render';

import Blocks from '../../containers/blocks.jsx';
import BlocksButtons from '../../containers/blocks-buttons.jsx';
import CostumeTab from '../../containers/costume-tab.jsx';
import TargetPane from '../../containers/target-pane.jsx';
import SoundTab from '../../containers/sound-tab.jsx';
import StageWrapper from '../../containers/stage-wrapper.jsx';
import Loader from '../loader/loader.jsx';
import Box from '../box/box.jsx';
import MenuBar from '../menu-bar/menu-bar.jsx';
import CostumeLibrary from '../../containers/costume-library.jsx';
import BackdropLibrary from '../../containers/backdrop-library.jsx';
import ImportModal from '../../containers/import-modal.jsx';
import WebGlModal from '../../containers/webgl-modal.jsx';
import TipsLibrary from '../../containers/tips-library.jsx';
import TipsButtonComponent from '../tips-button/tips-button.jsx';
import Cards from '../../containers/cards.jsx';
import DragLayer from '../../containers/drag-layer.jsx';

import layout, {STAGE_SIZE_MODES} from '../../lib/layout-constants';
import {resolveStageSize} from '../../lib/screen-utils';

import styles from './mw-gui.css';
import codeIcon from './icon--code.svg';
import costumesIcon from './icon--costumes.svg';
import soundsIcon from './icon--sounds.svg';

// Cache this value to only retreive it once the first time.
// Assume that it doesn't change for a session.
let isRendererSupported = null;

const MwGUIComponent = props => {
    const {
        activeTabIndex,
        basePath,
        blocksTabVisible,
        cardsVisible,
        children,
        costumesTabVisible,
        importInfoVisible,
        handleLoadProject,
        isMw,
        isPlayerOnly,
        loading,
        localSave,
        onActivateCostumesTab,
        onActivateSoundsTab,
        onActivateTab,
        onOpenTipLibrary,
        viewerMode,
        soundsTabVisible,
        stageSizeMode,
        tipsLibraryVisible,
        theme,
        vm,
        onboardingDeckId,
        ...componentProps
    } = omit(props, 'dispatch');
    if (children) {
        return (
            <Box {...componentProps}>{children}</Box>
        );
    }

    const tabClassNames = {
        tabs: styles.tabs,
        tab: classNames(tabStyles.reactTabsTab, styles.tab),
        tabList: classNames(tabStyles.reactTabsTabList, styles.tabList),
        tabPanel: classNames(tabStyles.reactTabsTabPanel, styles.tabPanel),
        tabPanelSelected: classNames(tabStyles.reactTabsTabPanelSelected, styles.isSelected),
        tabSelected: classNames(tabStyles.reactTabsTabSelected, styles.isSelected)
    };

    if (isRendererSupported === null) {
        isRendererSupported = Renderer.isSupported();
    }

    return (
        <MediaQuery minWidth={layout.fullSizeMinWidth}>
            {isFullSize => {
                const stageSize = resolveStageSize(stageSizeMode, isFullSize || isPlayerOnly);

                return isPlayerOnly ? (
                    <StageWrapper
                        isRendererSupported={isRendererSupported}
                        stageSize={stageSize}
                        vm={vm}
                    />
                ) : (
                    <Box
                        className={styles.pageWrapper}
                        {...componentProps}
                    >
                        {loading ? (
                            <Loader theme={theme} />
                        ) : null}
                        {importInfoVisible ? (
                            <ImportModal />
                        ) : null}
                        {isRendererSupported ? null : (
                            <WebGlModal theme={theme} />
                        )}
                        {tipsLibraryVisible ? (
                            <TipsLibrary />
                        ) : null}
                        {cardsVisible && !viewerMode ? (
                            <Cards />
                        ) : null}
                        {/* {costumeLibraryVisible ? (
                                <CostumeLibrary
                                    vm={vm}
                                    onRequestClose={onRequestCloseCostumeLibrary}
                                />
                            ) : null}
                            {backdropLibraryVisible ? (
                                <BackdropLibrary
                                    vm={vm}
                                    onRequestClose={onRequestCloseBackdropLibrary}
                                />
                            ) : null} */}
                        <MenuBar
                            handleLoadProject={handleLoadProject}
                            menuBarVisible={localSave}
                        />
                        <Box
                            className={classNames(styles.bodyWrapper,
                                localSave ? styles.bodyWrapperWithMenu : styles.bodyWrapperNoMenu)}
                        >
                            <Box className={styles.flexWrapper}>
                                <Box
                                    className={classNames(styles.editorWrapper,
                                        {[styles.viewerMode]: viewerMode}
                                    )}
                                >
                                    <TipsButtonComponent
                                        theme={theme}
                                        onClick={onOpenTipLibrary}
                                    />
                                    <Tabs
                                        forceRenderTabPanel
                                        className={tabClassNames.tabs}
                                        selectedIndex={activeTabIndex}
                                        selectedTabClassName={tabClassNames.tabSelected}
                                        selectedTabPanelClassName={tabClassNames.tabPanelSelected}
                                        onSelect={onActivateTab}
                                    >
                                        <TabList className={tabClassNames.tabList}>
                                            <Tab
                                                className={classNames(tabClassNames.tab,
                                                    {[styles.hidden]: isMw})}
                                            >
                                                <img
                                                    draggable={false}
                                                    src={codeIcon}
                                                />
                                                <FormattedMessage
                                                    defaultMessage="Code"
                                                    description="Button to get to the code panel"
                                                    id="gui.gui.codeTab"
                                                />
                                            </Tab>
                                            {!isMw && <Tab
                                                className={tabClassNames.tab}
                                                onClick={onActivateCostumesTab}
                                            >
                                                <img
                                                    draggable={false}
                                                    src={costumesIcon}
                                                />
                                                <FormattedMessage
                                                    defaultMessage="Costumes"
                                                    description="Button to get to the costumes panel"
                                                    id="gui.gui.costumesTab"
                                                />}
                                            </Tab>}
                                            {!isMw && <Tab
                                                className={tabClassNames.tab}
                                                onClick={onActivateSoundsTab}
                                            >
                                                <img
                                                    draggable={false}
                                                    src={soundsIcon}
                                                />
                                                <FormattedMessage
                                                    defaultMessage="Sounds"
                                                    description="Button to get to the sounds panel"
                                                    id="gui.gui.soundsTab"
                                                />
                                            </Tab>}
                                        </TabList>
                                        <TabPanel className={tabClassNames.tabPanel}>
                                            <Box className={styles.blocksWrapper}>
                                                {isMw && !viewerMode && <BlocksButtons />}
                                                <Blocks
                                                    grow={1}
                                                    isVisible={blocksTabVisible} // Blocks tab
                                                    options={{
                                                        media: `${basePath}static/blocks-media/`
                                                    }}
                                                    stageSize={stageSize}
                                                    vm={vm}
                                                />
                                            </Box>
                                        </TabPanel>
                                        {!isMw && <TabPanel className={tabClassNames.tabPanel}>
                                            {costumesTabVisible ?
                                                <CostumeTab
                                                    showEditor={!isMw}
                                                    vm={vm}
                                                /> :
                                                null}
                                        </TabPanel>}
                                        {!isMw && <TabPanel className={tabClassNames.tabPanel}>
                                            {soundsTabVisible ? <SoundTab vm={vm} /> : null}
                                        </TabPanel>}
                                    </Tabs>
                                </Box>

                                <Box className={classNames(styles.stageAndTargetWrapper, styles[stageSize])}>
                                    <StageWrapper
                                        isRendererSupported={isRendererSupported}
                                        stageSize={stageSize}
                                        vm={vm}
                                    />
                                    <Box className={styles.targetWrapper}>
                                        <TargetPane
                                            stageSize={stageSize}
                                            vm={vm}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        <DragLayer />
                    </Box>);
            }}
        </MediaQuery>);
};
MwGUIComponent.propTypes = {
    activeTabIndex: PropTypes.number,
    backdropLibraryVisible: PropTypes.bool,
    basePath: PropTypes.string,
    blocksTabVisible: PropTypes.bool,
    cardsVisible: PropTypes.bool,
    children: PropTypes.node,
    costumeLibraryVisible: PropTypes.bool,
    costumesTabVisible: PropTypes.bool,
    handleLoadProject: PropTypes.func,
    importInfoVisible: PropTypes.bool,
    intl: intlShape,
    isPlayerOnly: PropTypes.bool,
    isMw: PropTypes.bool,
    loading: PropTypes.bool,
    localSave: PropTypes.bool,
    onActivateCostumesTab: PropTypes.func,
    onActivateSoundsTab: PropTypes.func,
    onActivateTab: PropTypes.func,
    onOpenTipLibrary: PropTypes.func,
    onRequestCloseBackdropLibrary: PropTypes.func,
    onRequestCloseCostumeLibrary: PropTypes.func,
    onTabSelect: PropTypes.func,
    onboardingDeckId: PropTypes.string,
    viewerMode: PropTypes.bool,
    soundsTabVisible: PropTypes.bool,
    stageSizeMode: PropTypes.oneOf(Object.keys(STAGE_SIZE_MODES)),
    theme: PropTypes.number,
    tipsLibraryVisible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};
MwGUIComponent.defaultProps = {
    basePath: '../scratch/',
    stageSizeMode: STAGE_SIZE_MODES.large
};

const mapStateToProps = state => ({
    // This is the button's mode, as opposed to the actual current state
    stageSizeMode: state.scratchGui.stageSize.stageSize
});

export default connect(mapStateToProps)(MwGUIComponent);
